import {
  booleanAttribute,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  EventTrackerService,
  SteeringPath,
  SteeringWheelService,
} from '@splixer/player';
import { SteeringWheelButtonComponent } from './steering-wheel-button.component';
import { NgClass } from '@angular/common';
import { uString } from '@hidat/huijs-common';
import { SteeringMode, SteeringReason, SteeringReasons } from '@splixer/common';

@Component({
  selector: 'kexp-steering-wheel',
  templateUrl: 'steering-wheel.component.html',
  styleUrl: 'steering-wheel.component.scss',
  imports: [SteeringWheelButtonComponent, NgClass],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class KexpSteeringWheelComponent implements OnInit {
  public steeringMode = input.required<SteeringMode>();
  public onDialog = input(false, { transform: booleanAttribute });
  public pathTaken = output<SteeringPath | undefined>();

  protected steeringWheelService = inject(SteeringWheelService);
  protected eventTracker = inject(EventTrackerService);
  protected loadingPaths = signal(true);
  protected pathsLoaded = false;

  protected leftPath = signal<SteeringPath | undefined>(undefined);
  protected rightPath = signal<SteeringPath | undefined>(undefined);
  protected straightPath = signal<SteeringPath | undefined>(undefined);
  protected steeringButtonTitle = signal<string>('Loading...');

  ngOnInit() {
    this.refreshPaths();
  }

  protected straightTitle = computed(() => {
    const path = this.straightPath();
    if (path) {
      return this.pathTitle(path.steeringReason);
    }
    return undefined;
  });

  protected leftTitle = computed(() => {
    const path = this.leftPath();
    if (path) {
      return this.pathTitle(path.steeringReason);
    }
    return undefined;
  });

  protected rightTitle = computed(() => {
    const path = this.rightPath();
    if (path) {
      return this.pathTitle(path.steeringReason);
    }
    return undefined;
  });

  protected pathTitle(steeringReason: SteeringReasons): string | undefined {
    // This needs to go into a vocab service
    let title = steeringReason;
    switch (steeringReason) {
      case SteeringReason.RandomMix:
      case SteeringReason.RandomShow:
        title = 'Something Different';
        break;
      case SteeringReason.RelatedShow:
        title = 'Related Show';
        break;
      case SteeringReason.RelatedMix:
        title = 'Related Mix';
        break;
      case SteeringReason.NextShowForProgram:
        title = 'Next Episode of Show';
        break;
      case SteeringReason.NextShowInSchedule:
        title = 'Broadcasted Next';
        break;
      case SteeringReason.ScheduledShow:
        title = 'Scheduled To Play Next';
        break;
    }
    return title;
  }

  protected getSteeringButtonTitle(): string {
    return this.steeringWheelService.steeringButtonTitle(this.steeringMode());
  }

  protected wrapperClasses = computed(() => {
    if (this.onDialog()) {
      return 'on-dialog';
    }
    return 'on-page';
  });

  protected straightPathClasses = computed<string | string[] | undefined>(
    () => {
      let klass: uString;
      const sm = this.steeringMode();
      if (
        sm === SteeringMode.ChangeDirection ||
        sm === SteeringMode.ChooseNextMix
      ) {
        klass = 'active';
      }
      return klass;
    },
  );

  protected refreshPaths() {
    const steeringMode = this.steeringMode();
    if (this.steeringWheelService.needsPaths()) {
      if (!this.pathsLoaded) {
        this.loadingPaths.set(true);
      }
      this.steeringWheelService.getPaths(steeringMode).subscribe((paths) => {
        this.pathsLoaded = true;
        this.straightPath.set(
          this.steeringWheelService.getStraightPath(steeringMode),
        );
        this.rightPath.set(this.steeringWheelService.getRightPath());
        this.leftPath.set(this.steeringWheelService.getLeftPath());
        this.steeringButtonTitle.set(this.getSteeringButtonTitle());
        this.loadingPaths.set(false);
      });
    } else {
      console.debug('Loading alt paths from cache');
      this.straightPath.set(
        this.steeringWheelService.getStraightPath(steeringMode),
      );
      this.rightPath.set(this.steeringWheelService.getRightPath());
      this.leftPath.set(this.steeringWheelService.getLeftPath());
      this.steeringButtonTitle.set(this.getSteeringButtonTitle());
      this.loadingPaths.set(false);
    }
  }

  protected shufflePathsEvent = () => {
    this.eventTracker.steeringWheelShuffle(
      'steering_wheel',
      this.steeringMode(),
    );
    this.refreshPaths();
  };

  protected takePath = (path: SteeringPath) => {
    const sm = this.steeringMode();
    this.eventTracker.pathTaken('steering_wheel', sm, path.steeringReason);
    this.steeringWheelService.skipToPath(path);
    this.pathTaken.emit(path);
  };
}
